import React from 'react';
import type { FmDataDisplayRule } from '../../../types/fm-data-value';
import type { FmLegendEntry } from '../../../types/fm-legend';
import { FmBanner } from '../FmBanner';
import { FmEmpty, FmSkeleton } from '../FmFeedback';
import { FmFigure } from '../FmFigure';
import '../ui.css';
import { headingLabelText, useHostedHeading } from '../fmHostedHeading';
import { formatChartUnit } from './FmChartChrome.constants';
import { useChartBlockCopy } from './chartCopy';
import { FmChartBlockNotice } from './FmChartBlockNotice';
import {
  computeChartBlockFigure,
  computeChartBlockState,
  DEFAULT_MIN_POINTS_FOR_CHART,
  resolveLegend,
  type FmChartBlockDisplayMode,
  type FmChartBlockPoint,
} from './chartBlockModel';
import './FmChartBlock.css';

export type { FmChartBlockPoint } from './chartBlockModel';

export interface FmChartBlockRenderContext {
  data: FmChartBlockPoint[];
  availableData: FmChartBlockPoint[];
  pointCount: number;
  unit: string;
  maxBarWidth: number;
  maxPlotWidth: number;
}

export interface FmChartBlockProps {
  title: string;
  /**
   * The surrounding surface ALREADY printed this title, so the block renders
   * titleless. Only needed where no published host can
   * reach the conclusion: a titled host that is not an FmSectionBand, or a
   * ReactNode title with no comparable plain text.
   */
  hosted?: boolean;
  data: FmChartBlockPoint[];
  /** Required unit for axis, labels, and sparse fallback stats. */
  unit: string;
  /**
   * The plot already prints the unit (axis title or unit-suffixed values), so
   * the block does not print it a second time beside the caption.
   */
  unitInAxis?: boolean;
  /** Required caption, rendered outside the chart frame. */
  caption: React.ReactNode;
  /** Required plain-language interpretation, rendered outside the chart frame. */
  takeaway: React.ReactNode;
  /** Optional secondary narrative detail, rendered outside the chart frame. */
  contextDetail?: React.ReactNode;
  chart?: React.ReactNode | ((context: FmChartBlockRenderContext) => React.ReactNode);
  legend?: FmLegendEntry[];
  valueKind?: FmDataDisplayRule['kind'];
  /** Minimum point count for chart rendering when sparseMode stays on data fallback. */
  minPointsForChart?: number;
  /**
   * Defaults to sparse data callouts. Use chart only for intentional compact,
   * capped bar modules or interaction cases.
   */
  sparseMode?: 'data' | 'chart';
  maxBarWidth?: number;
  maxPlotWidth?: number;
  loading?: boolean;
  error?: string | null;
  emptyText?: string;
  source?: React.ReactNode;
  explanationTitle?: string;
  /** Default keeps the legacy framed figure shell; story is an unframed chart + narrative split. */
  variant?: FmChartBlockVariant;
  locale?: string;
  unavailableLabel?: string;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

type FmChartBlockVariant = 'figure' | 'story';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

function renderChartBlockVisual({
  loading,
  error,
  figure,
  emptyText,
}: {
  loading: boolean;
  error?: string | null;
  figure?: React.ReactNode;
  emptyText?: string;
}): React.ReactNode {
  if (loading) {
    return <FmSkeleton rows={4} />;
  }
  if (error) {
    return <FmBanner tone="error" title={error} />;
  }
  if (!figure && emptyText) {
    return <FmEmpty description={emptyText} />;
  }
  return figure;
}

interface ChartBlockStoryProps {
  displayMode: FmChartBlockDisplayMode;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  dataTestId?: string;
  loading: boolean;
  error?: string | null;
  figure?: React.ReactNode;
  emptyText?: string;
  title: string;
  /**
   * false when the host section already printed this title, so the story copy
   * drops it rather than saying it twice. FmChartBlock resolves it once for
   * the block, so the notice and both variants agree.
   */
  showTitle: boolean;
  explanationTitle?: string;
  explanation: React.ReactNode;
  source?: React.ReactNode;
}

/** The story variant: an unframed plot beside its narrative copy. */
const ChartBlockStory: React.FC<ChartBlockStoryProps> = ({
  displayMode,
  className,
  dataTestId,
  loading,
  error,
  figure,
  emptyText,
  title,
  showTitle,
  explanationTitle,
  explanation,
  source,
}) => {
  return (
    <section
      className={cx(
        'fm-chart-block',
        `fm-chart-block--${displayMode}`,
        'fm-chart-block--story',
        className,
      )}
      // Titleless still names the region: dropping a DUPLICATE title must not
      // cost a screen reader the block's name.
      aria-label={showTitle ? undefined : headingLabelText(title)}
      data-testid={dataTestId ?? 'fm-chart-block'}
    >
      <div className="fm-chart-block__story-layout">
        <div className="fm-chart-block__story-plot">
          {renderChartBlockVisual({ loading, error, figure, emptyText })}
        </div>
        <div className="fm-chart-block__story-copy" data-testid="fm-chart-block-story-copy">
          {showTitle ? <p className="fm-chart-block__story-title">{title}</p> : null}
          {explanationTitle ? (
            <p className="fm-chart-block__story-explanation-title">{explanationTitle}</p>
          ) : null}
          {explanation}
          {source ? <div className="fm-chart-block__source">{source}</div> : null}
        </div>
      </div>
    </section>
  );
};

/** Takeaway, detail, caption and (when no axis carries it) the unit, outside the frame. */
const ChartBlockExplanation: React.FC<{
  takeaway: React.ReactNode;
  takeawayId: string;
  contextDetail?: React.ReactNode;
  caption: React.ReactNode;
  unit: string | null;
}> = ({ takeaway, takeawayId, contextDetail, caption, unit }) => (
  <div className="fm-chart-block__context" data-testid="fm-chart-block-context">
    {takeaway ? (
      <div className="fm-chart-block__takeaway" id={takeawayId}>
        {takeaway}
      </div>
    ) : null}
    {contextDetail ? <div className="fm-chart-block__detail">{contextDetail}</div> : null}
    {caption ? <div className="fm-chart-block__caption">{caption}</div> : null}
    {unit === null ? null : <div className="fm-chart-block__unit">{unit}</div>}
  </div>
);

/** The one-line notice when nothing is loading, nothing failed and nothing is plotted. */
function chartBlockNotice(args: {
  loading: boolean;
  error?: string | null;
  pointCount: number;
  title: string;
  showTitle: boolean;
  text?: string;
  emptyText: string;
  className?: string;
  testId?: string;
}): React.ReactElement | null {
  if (args.loading || args.error || args.pointCount > 0) return null;
  return (
    <FmChartBlockNotice
      title={args.title}
      showTitle={args.showTitle}
      text={args.text ?? args.emptyText}
      className={args.className}
      data-testid={args.testId ?? 'fm-chart-block'}
    />
  );
}

export const FmChartBlock: React.FC<FmChartBlockProps> = ({
  title,
  hosted,
  data,
  unit: rawUnit,
  unitInAxis = false,
  caption: rawCaption,
  takeaway: rawTakeaway,
  contextDetail: rawContextDetail,
  chart,
  legend,
  valueKind = 'number',
  minPointsForChart = DEFAULT_MIN_POINTS_FOR_CHART,
  sparseMode = 'data',
  maxBarWidth,
  maxPlotWidth,
  loading = false,
  error,
  emptyText: emptyTextProp,
  source: sourceProp,
  explanationTitle: explanationTitleProp,
  variant = 'figure',
  locale = 'fi-FI',
  unavailableLabel,
  className,
  'data-testid': dataTestId,
}) => {
  const {
    availableData,
    pointCount,
    safeMaxBarWidth,
    safeMaxPlotWidth,
    shouldRenderChart,
    displayMode,
    sparseItems,
  } = computeChartBlockState({
    data,
    minPointsForChart,
    sparseMode,
    chart,
    maxBarWidth,
    maxPlotWidth,
  });
  const { emptyText, explanationTitle, takeaway, contextDetail, caption, source } =
    useChartBlockCopy({
      title,
      emptyText: emptyTextProp,
      explanationTitle: explanationTitleProp,
      copy: {
        takeaway: rawTakeaway,
        contextDetail: rawContextDetail,
        caption: rawCaption,
        source: sourceProp,
      },
    });
  const unit = formatChartUnit(rawUnit);
  const legendEntries = resolveLegend(data, legend);
  const takeawayId = `fm-chart-takeaway-${React.useId().replace(/:/g, '')}`;
  // One section = one title: a titled host already printed this heading, so the
  // story variant drops it rather than saying it twice.
  // The figure variant hands the same question to FmFigure via `hosted`.
  const storyHeading = useHostedHeading(title, 3, hosted);

  const figure = computeChartBlockFigure({
    pointCount,
    shouldRenderChart,
    chart,
    data,
    availableData,
    unit,
    safeMaxBarWidth,
    safeMaxPlotWidth,
    sparseItems,
    caption,
    valueKind,
    locale,
    unavailableLabel,
    legend: legendEntries,
    accessibleName: headingLabelText(title) ?? title,
    takeawayId: takeaway ? takeawayId : undefined,
  });

  // The sparse fallback carries the unit in its own stats and a refusal has no
  // values to qualify, so the unit line only accompanies a drawn plot.
  const railUnit = unitInAxis || !shouldRenderChart ? null : unit;
  const explanation = (
    <ChartBlockExplanation
      takeaway={takeaway}
      takeawayId={takeawayId}
      contextDetail={contextDetail}
      caption={caption}
      unit={railUnit}
    />
  );

  const notice = chartBlockNotice({
    loading,
    error,
    pointCount,
    title,
    showTitle: storyHeading.render,
    text: unavailableLabel,
    emptyText,
    className,
    testId: dataTestId,
  });
  if (notice) return notice;

  if (variant === 'story') {
    return (
      <ChartBlockStory
        displayMode={displayMode}
        className={className}
        dataTestId={dataTestId}
        loading={loading}
        error={error}
        figure={figure}
        emptyText={emptyText}
        title={title}
        showTitle={storyHeading.render}
        explanationTitle={explanationTitle}
        explanation={explanation}
        source={source}
      />
    );
  }

  return (
    <FmFigure
      title={title}
      hosted={hosted}
      figure={figure}
      explanation={explanation}
      explanationTitle={explanationTitle}
      loading={loading}
      error={error}
      emptyText={emptyText}
      source={source}
      className={cx('fm-chart-block', `fm-chart-block--${displayMode}`, className)}
      data-testid={dataTestId ?? 'fm-chart-block'}
    />
  );
};

export default FmChartBlock;
